import { Box, Stack, Typography } from '@mui/material';
import React, { Fragment, ReactNode } from 'react';

import { usePermission } from '@sbu/hooks.permissions';

import { FavoriteFieldType } from 'core/graphql';
import { FavoriteFieldContainer } from 'shared/components/internal/FavoriteField';
import { RecordType, RecordValue } from 'shared/components/internal/Records';
import { type EntityTypeFieldsAndIndicator } from 'shared/components/internal/Records/useEntityTypeRecordFields';
import { humanizeToCorrectTitleCase } from 'shared/utils';

interface RecordPropertyProps {
  children: ReactNode;
  isExtractedField?: boolean;
  label?: string;
  name: string;
  record: RecordType;
  entityTypeFieldsAndIndicator?: EntityTypeFieldsAndIndicator;
}

export interface RecordPropertyForDisplay {
  name: string;
  label?: string;
  value: ReactNode;
}

export const emptyRecordValues: ReactNode[] = ['', null, undefined];

export const getPropertyLabel = (
  name: string,
  label?: string,
  isExtractedField?: boolean
) => {
  if (isExtractedField) {
    return label === undefined ? name : label;
  } else {
    return humanizeToCorrectTitleCase(label === undefined ? name : label);
  }
};

export const RecordProperty = ({
  children,
  isExtractedField,
  label,
  name,
  record,
  entityTypeFieldsAndIndicator,
}: RecordPropertyProps) => {
  const canManageFavoriteFields = usePermission('can_manage_favorite_fields');
  const fieldHasIndicator =
    entityTypeFieldsAndIndicator?.entityTypeFields?.find(
      (field) => field.field === name
    );

  /* TODO: recreate grid/flexbox and add dl/dt/dd tags instead of div */
  const content = (
    <Box
      marginBottom={1.25}
      overflow='hidden'
      sx={{ breakInside: 'avoid-column', wordBreak: 'break-word' }}
    >
      <Typography variant='subtitle1'>
        {getPropertyLabel(name, label, isExtractedField)}
      </Typography>
      <Stack alignItems='center' spacing={0.5} direction='row'>
        <Box
          component={RecordValue}
          fontSize='1.125rem'
          name={name}
          record={record}
          entityIndicatorData={
            fieldHasIndicator
              ? entityTypeFieldsAndIndicator?.entityIndicatorData
              : undefined
          }
        >
          {emptyRecordValues.includes(children) ? (
            <Box fontStyle='italic'>None</Box>
          ) : (
            children
          )}
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Fragment>
      <FavoriteFieldContainer
        disabled={!canManageFavoriteFields}
        fieldName={name}
        fieldType={FavoriteFieldType.Record}
        objectType={
          record.hasOwnProperty('objectType') ? record.objectType : undefined
        }
      >
        {content}
      </FavoriteFieldContainer>
    </Fragment>
  );
};
